import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { apiService } from '@/services/api'

/**
 * AI generation phases that indicate processing is still active
 */
const ACTIVE_PHASES = [
  'researching',
  'generating_headlines',
  'outlining',
  'writing',
  'optimizing'
] as const

/**
 * Terminal phases where polling should stop
 */
const TERMINAL_PHASES = [
  'ready',
  'failed',
  'awaiting_headline_approval',
  'awaiting_outline_approval',
  'awaiting_length_decision'
] as const

interface BlogPostResponse {
  id: string
  title: string
  content: string
  status: string
  ai_metadata?: {
    phase?: string
    error?: string
    cost?: number
    tokens_used?: number
    word_count?: number
    length_warning?: boolean
    seo_warnings?: string[]
  }
  [key: string]: unknown
}

interface UseBlogGenerationPollingOptions {
  /** Post ID to poll for updates */
  postId: string
  /** Whether polling is enabled (default: true) */
  enabled?: boolean
  /** Callback when phase changes */
  onPhaseChange?: (phase: string) => void
}

interface UseBlogGenerationPollingReturn {
  /** Current AI generation phase */
  currentPhase: string | undefined
  /** Whether the query is loading */
  isLoading: boolean
  /** Error if the query failed */
  error: Error | null
  /** The full post data */
  post: BlogPostResponse | undefined
  /** Whether polling is currently active */
  isPolling: boolean
}

/**
 * Custom hook to poll a blog post for AI generation updates
 *
 * Polls every 2 seconds while the post is in an active generation phase.
 * Automatically stops polling when reaching a terminal phase (ready, failed, or approval phases).
 * Cleans up polling when the component unmounts.
 *
 * @param options - Configuration options
 * @returns Post data, loading state, error, and current phase
 *
 * @example
 * const { currentPhase, isLoading, post } = useBlogGenerationPolling({
 *   postId: 'post-123',
 *   onPhaseChange: (phase) => {
 *     if (phase === 'awaiting_headline_approval') {
 *       // Show headline approval dialog
 *     }
 *   }
 * })
 */
export function useBlogGenerationPolling({
  postId,
  enabled = true,
  onPhaseChange
}: UseBlogGenerationPollingOptions): UseBlogGenerationPollingReturn {
  const queryClient = useQueryClient()

  const {
    data: post,
    isLoading,
    error,
    isRefetching
  } = useQuery<BlogPostResponse, Error>({
    queryKey: ['blog-post', postId],
    queryFn: async () => {
      const data = await apiService.blog.posts.get(postId)
      console.log('[useBlogGenerationPolling] Fetched post data:', {
        postId,
        phase: data.ai_metadata?.phase,
        timestamp: new Date().toISOString()
      })
      return data as BlogPostResponse
    },
    enabled: enabled && !!postId,
    refetchInterval: (data) => {
      const currentPost = data.state.data
      const phase = currentPost?.ai_metadata?.phase

      // FIX: Use dataUpdatedAt to detect if we're getting stale data
      const lastUpdate = data.state.dataUpdatedAt
      const now = Date.now()
      const staleness = now - lastUpdate

      console.log('[useBlogGenerationPolling] refetchInterval check:', {
        postId,
        phase,
        staleness: `${staleness}ms`,
        isTerminal: phase ? TERMINAL_PHASES.includes(phase as typeof TERMINAL_PHASES[number]) : false,
        isActive: phase ? ACTIVE_PHASES.includes(phase as typeof ACTIVE_PHASES[number]) : false
      })

      // If no phase yet, continue polling to wait for backend to set it
      if (!phase) {
        console.log('[useBlogGenerationPolling] No phase yet, continuing polling')
        return 2000
      }

      // Stop polling if terminal phase reached
      if (TERMINAL_PHASES.includes(phase as typeof TERMINAL_PHASES[number])) {
        console.log('[useBlogGenerationPolling] Terminal phase reached, stopping polling:', phase)
        return false
      }

      // Continue polling every 2 seconds for active phases
      // FIX: If data is stale (> 3 seconds old) during active phase, poll more aggressively
      if (staleness > 3000) {
        console.log('[useBlogGenerationPolling] Data is stale, polling immediately')
        return 1000 // Poll every 1 second when data is stale
      }

      console.log('[useBlogGenerationPolling] Active phase, continuing polling:', phase)
      return 2000
    },
    refetchIntervalInBackground: false, // Don't poll when tab is not focused
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (renamed from cacheTime in React Query v5)
    retry: 3, // Retry up to 3 times on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
  })

  const currentPhase = post?.ai_metadata?.phase
  const isPolling = isRefetching && !!currentPhase && ACTIVE_PHASES.includes(currentPhase as any)

  // Notify on phase changes
  useEffect(() => {
    if (currentPhase && onPhaseChange) {
      console.log('[useBlogGenerationPolling] Phase changed, notifying:', currentPhase)
      onPhaseChange(currentPhase)
    }
  }, [currentPhase, onPhaseChange])

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      console.log('[useBlogGenerationPolling] Cleaning up polling for:', postId)
      queryClient.cancelQueries({ queryKey: ['blog-post', postId] })
    }
  }, [postId, queryClient])

  // Log current state for debugging
  useEffect(() => {
    console.log('[useBlogGenerationPolling] State update:', {
      postId,
      currentPhase,
      isPolling,
      isRefetching,
      isLoading
    })
  }, [postId, currentPhase, isPolling, isRefetching, isLoading])

  return {
    currentPhase,
    isLoading,
    error: error as Error | null,
    post,
    isPolling
  }
}
